import { useState } from 'react'
import { cn } from '@/lib/utils'
import { ChevronDown, ChevronUp } from 'lucide-react'

interface DataTableProps {
  data: Record<string, unknown>[]
  title?: string
  columns?: string[]
  maxRows?: number
  compact?: boolean
}

export function DataTable({
  data,
  title,
  columns: explicitColumns,
  maxRows = 5,
  compact = false,
}: DataTableProps) {
  const [expanded, setExpanded] = useState(false)

  if (!data || data.length === 0) return null

  // Auto-detect columns from first item, excluding internal fields
  const autoColumns = Object.keys(data[0]).filter((key) => {
    if (key.startsWith('_')) return false
    const value = data[0][key]
    // Exclude complex nested objects and arrays
    if (Array.isArray(value)) return false
    if (value !== null && typeof value === 'object') return false
    return true
  })

  const columns = explicitColumns || autoColumns.slice(0, 5) // Max 5 columns by default
  const displayData = expanded ? data : data.slice(0, maxRows)
  const hasMore = data.length > maxRows

  // Humanize column names
  const humanizeColumn = (col: string) =>
    col
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim()

  // Format cell values
  const formatCell = (value: unknown): string => {
    if (value === null || value === undefined) return '-'
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    if (typeof value === 'number') {
      if (value < 0.001 && value > 0) return value.toExponential(2)
      return value.toLocaleString(undefined, { maximumFractionDigits: 3 })
    }
    const str = String(value)
    return str.length > 50 ? str.slice(0, 47) + '...' : str
  }

  return (
    <div className="space-y-1">
      {title && (
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {title} ({data.length})
        </h4>
      )}
      <div className="overflow-x-auto rounded-md border border-border">
        <table className={cn('w-full text-left', compact ? 'text-[11px]' : 'text-xs')}>
          <thead>
            <tr className="bg-muted/50 border-b border-border">
              {columns.map((col) => (
                <th
                  key={col}
                  className={cn(
                    'font-semibold text-muted-foreground whitespace-nowrap',
                    compact ? 'px-2 py-1' : 'px-3 py-1.5',
                  )}
                >
                  {humanizeColumn(col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={cn(
                  'border-b border-border/50 last:border-0',
                  rowIndex % 2 === 0 ? 'bg-transparent' : 'bg-muted/20',
                )}
              >
                {columns.map((col) => (
                  <td
                    key={col}
                    className={cn('whitespace-nowrap', compact ? 'px-2 py-0.5' : 'px-3 py-1.5')}
                    title={String(row[col] ?? '')}
                  >
                    {formatCell(row[col])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <button
          onClick={() => setExpanded(!expanded)}
          className={cn(
            'flex items-center gap-1 text-xs text-muted-foreground',
            'hover:text-foreground transition-colors',
          )}
        >
          {expanded ? (
            <>
              <ChevronUp className="w-3 h-3" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" />
              Show {data.length - maxRows} more
            </>
          )}
        </button>
      )}
    </div>
  )
}
